import type {
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
} from "vue-router";

export type ShellMode = "full" | "none";

export interface ModuleRouteDefinition {
  path: string;
  name: string;
  vueModule: string;
  shellMode: ShellMode;
  requiresSession: boolean;
  shellTitleKey?: string;
  shellTitleQueryParam?: string;
  backMode?: "route" | "history";
  backRouteName?: string;
  requiresModule?: string;
  requiresLicense?: string;
}

export interface LegacyPlaceRequest {
  nameToken: string;
  params?: Record<string, string>;
}

type RouteStateLike = {
  name: RouteLocationNormalizedLoaded["name"] | null;
  params: RouteLocationNormalizedLoaded["params"];
  query: RouteLocationNormalizedLoaded["query"];
};

export const moduleRouteDefinitions: ModuleRouteDefinition[] = [
  {
    path: "/home",
    name: "home",
    vueModule: "HomeModule",
    shellMode: "full",
    requiresSession: true,
  },
  {
    path: "/checkin",
    name: "checkin",
    vueModule: "CheckinModule",
    shellMode: "full",
    requiresSession: true,
    requiresModule: "checkin",
  },
  {
    path: "/news",
    name: "news",
    vueModule: "NewsModule",
    shellMode: "full",
    requiresSession: true,
  },
  {
    path: "/documents",
    name: "documents",
    vueModule: "DocumentsModule",
    shellMode: "full",
    requiresSession: true,
    requiresModule: "documents",
  },
  {
    path: "/docbycategory",
    name: "docbycategory",
    vueModule: "DocByCategoryModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.documents.title",
    shellTitleQueryParam: "categoryName",
    backRouteName: "documents",
    requiresModule: "documents",
  },
  {
    path: "/procedures",
    name: "procedures",
    vueModule: "ProceduresModule",
    shellMode: "full",
    requiresSession: true,
    requiresModule: "procedures",
  },
  {
    path: "/vacations",
    name: "vacations",
    vueModule: "VacationsModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.vacations.title",
    backRouteName: "profile",
  },
  {
    path: "/vacationsdetail",
    name: "vacationsdetail",
    vueModule: "VacationsDetailModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.vacations.title",
    shellTitleQueryParam: "title",
    backRouteName: "vacations",
  },
  {
    path: "/expensesheets",
    name: "expensesheets",
    vueModule: "ExpenseSheetsModule",
    shellMode: "full",
    requiresSession: true,
    requiresModule: "expenses",
  },
  {
    path: "/expensesheet-detail/:id?",
    name: "expensesheetdetail",
    vueModule: "ExpenseSheetDetailDialog",
    shellMode: "full",
    requiresSession: true,
  },
  {
    path: "/profile",
    name: "profile",
    vueModule: "ProfileModule",
    shellMode: "full",
    requiresSession: true,
  },
  {
    path: "/projects",
    name: "projects",
    vueModule: "ProjectsModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.projects.title",
    backRouteName: "profile",
  },
  {
    path: "/training",
    name: "training",
    vueModule: "TrainingModule",
    shellMode: "full",
    requiresSession: true,
    requiresModule: "training",
  },
  {
    path: "/targethours",
    name: "targethours",
    vueModule: "TargetHoursModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.target-hours.title",
    backRouteName: "profile",
  },
  {
    path: "/history",
    name: "checkinhistory",
    vueModule: "CheckinHistoryOverviewModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.checkin.historico.title",
    backRouteName: "checkin",
    requiresModule: "checkin",
  },
  {
    path: "/historydetail",
    name: "checkinhistorydetail",
    vueModule: "CheckinHistoryDetailModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.checkin.historico-detalle.monthly_activity_detail",
    shellTitleQueryParam: "label",
    backMode: "history",
    backRouteName: "checkinhistory",
    requiresModule: "checkin",
  },
  {
    path: "/activitydetail",
    name: "activitydetail",
    vueModule: "ActivityDetailModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.activity-detail.title",
    backMode: "history",
    backRouteName: "checkin",
  },
  {
    path: "/survey",
    name: "survey",
    vueModule: "SurveyViewerModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.survey.title",
    backRouteName: "home",
  },
  {
    path: "/employeecalendar",
    name: "employeecalendar",
    vueModule: "EmployeeCalendar",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.employeeCalendar.title",
    backRouteName: "vacations",
  },
  {
    path: "/employee/:id?",
    name: "employee",
    vueModule: "EmployeeModule",
    shellMode: "full",
    requiresSession: true,
  },
  {
    path: "/hcalendar",
    name: "hcalendar",
    vueModule: "HolidayCalendarModule",
    shellMode: "full",
    requiresSession: true,
    shellTitleKey: "wkt.client.holiday-calendar.title",
    backRouteName: "procedures",
  },
  {
    path: "/form/:formId?",
    name: "form",
    vueModule: "FormViewerModule",
    shellMode: "full",
    requiresSession: true,
  },
  {
    path: "/review/:reviewId?/:phase?",
    name: "review",
    vueModule: "AssessmentReviewModule",
    shellMode: "full",
    requiresSession: true,
  },
  {
    path: "/kiosk",
    name: "kiosk",
    vueModule: "KioskModule",
    shellMode: "none",
    requiresSession: false,
  },
  {
    path: "/register",
    name: "register",
    vueModule: "RegisterModule",
    shellMode: "none",
    requiresSession: false,
  },
];

export const shellChromeRouteNames = new Set([
  "home",
  "news",
  "checkin",
  "procedures",
  "documents",
  "expensesheets",
  "training",
  "profile",
]);

const routeNameToModule = new Map(
  moduleRouteDefinitions.map((definition) => [
    definition.name,
    definition.vueModule,
  ]),
);

const moduleToRouteName = new Map(
  moduleRouteDefinitions.map((definition) => [
    definition.vueModule,
    definition.name,
  ]),
);

export function getRouteDefinitionByName(
  routeName: unknown,
): ModuleRouteDefinition | null {
  if (typeof routeName !== "string") {
    return null;
  }

  return (
    moduleRouteDefinitions.find(
      (definition) => definition.name === routeName,
    ) ?? null
  );
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function normalizePayload(payload: unknown): Record<string, unknown> | null {
  if (!payload) {
    return null;
  }

  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload) as Record<string, unknown>;
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  return typeof payload === "object"
    ? (payload as Record<string, unknown>)
    : null;
}

function queryValue(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  return undefined;
}

export function getVueModuleForRouteName(routeName: unknown): string | null {
  if (typeof routeName !== "string") {
    return null;
  }

  return routeNameToModule.get(routeName) ?? null;
}

export function getShellModeForRouteName(routeName: unknown): ShellMode {
  if (typeof routeName !== "string") {
    return "none";
  }

  return (
    moduleRouteDefinitions.find((definition) => definition.name === routeName)
      ?.shellMode ?? "none"
  );
}

export function shouldUseShellChrome(routeName: unknown): boolean {
  if (typeof routeName !== "string") {
    return false;
  }

  if (shellChromeRouteNames.has(routeName)) {
    return true;
  }

  const routeDefinition = getRouteDefinitionByName(routeName);
  return Boolean(
    routeDefinition?.backRouteName || routeDefinition?.shellTitleKey,
  );
}

export function getRouteLocationForModule(
  moduleName: string,
  payload?: unknown,
): RouteLocationRaw | null {
  const normalizedPayload = normalizePayload(payload);
  const routeName = moduleToRouteName.get(moduleName);

  if (!routeName) {
    return null;
  }

  switch (moduleName) {
    case "EmployeeModule": {
      const employeeId = toNumber(
        normalizedPayload?.employeeId ?? normalizedPayload?.id,
      );
      return {
        name: routeName,
        params: employeeId == null ? {} : { id: employeeId },
      };
    }
    case "FormViewerModule": {
      const formId = toNumber(normalizedPayload?.formId);
      return {
        name: routeName,
        params: formId == null ? {} : { formId },
      };
    }
    case "AssessmentReviewModule": {
      const reviewId = toNumber(normalizedPayload?.reviewId);
      const phase = toNumber(normalizedPayload?.phase);
      return {
        name: routeName,
        params: {
          ...(reviewId == null ? {} : { reviewId }),
          ...(phase == null ? {} : { phase }),
        },
      };
    }
    case "SurveyViewerModule": {
      const surveyId = toNumber(normalizedPayload?.surveyId);
      return {
        name: routeName,
        query: Object.fromEntries(
          Object.entries({
            surveyId: queryValue(surveyId),
          }).filter(([, value]) => value != null),
        ),
      };
    }
    case "ExpenseSheetDetailDialog": {
      const id = toNumber(normalizedPayload?.id);
      const returnModule = queryValue(normalizedPayload?.returnModule);
      return {
        name: routeName,
        params: id == null ? {} : { id },
        query: returnModule ? { returnModule } : undefined,
      };
    }
    case "ActivityDetailModule": {
      const activityId = toNumber(
        normalizedPayload?.activityId ?? normalizedPayload,
      );
      return {
        name: routeName,
        query: Object.fromEntries(
          Object.entries({
            activityId: queryValue(activityId),
          }).filter(([, value]) => value != null),
        ),
      };
    }
    case "DocByCategoryModule": {
      const currentCategory =
        normalizedPayload?.currentCategory &&
        typeof normalizedPayload.currentCategory === "object"
          ? (normalizedPayload.currentCategory as Record<string, unknown>)
          : null;
      const categoryId = toNumber(
        currentCategory?.id ?? normalizedPayload?.categoryId,
      );
      const categoryName = queryValue(
        currentCategory?.name ?? normalizedPayload?.categoryName,
      );
      return {
        name: routeName,
        query: Object.fromEntries(
          Object.entries({
            categoryId: queryValue(categoryId),
            categoryName,
          }).filter(([, value]) => value != null),
        ),
      };
    }
    case "VacationsDetailModule": {
      const bagId = normalizedPayload?.bagId;
      return {
        name: routeName,
        query: Object.fromEntries(
          Object.entries({
            bagId:
              bagId === null
                ? "null"
                : queryValue(toNumber(bagId) ?? queryValue(bagId)),
          }).filter(([, value]) => value != null),
        ),
      };
    }
    case "HolidayCalendarModule":
    case "EmployeeCalendar": {
      const query = {
        rid: queryValue(normalizedPayload?.requestId ?? normalizedPayload?.rid),
        employeeCalendar: queryValue(normalizedPayload?.employeeCalendar),
        year: queryValue(normalizedPayload?.year),
        month: queryValue(normalizedPayload?.month),
      };
      return {
        name: routeName,
        query: Object.fromEntries(
          Object.entries(query).filter(([, value]) => value != null),
        ),
      };
    }
    default:
      return { name: routeName };
  }
}

export function getRevealPayloadFromRoute(
  moduleName: string,
  route: RouteLocationNormalizedLoaded,
): unknown {
  switch (moduleName) {
    case "EmployeeModule": {
      const employeeId = toNumber(route.params.id);
      return employeeId == null ? undefined : { employeeId };
    }
    case "FormViewerModule": {
      const formId = toNumber(route.params.formId);
      return formId == null ? undefined : { formId };
    }
    case "AssessmentReviewModule": {
      const reviewId = toNumber(route.params.reviewId);
      const phase = toNumber(route.params.phase);
      if (reviewId == null && phase == null) {
        return undefined;
      }
      return { reviewId, phase };
    }
    case "SurveyViewerModule": {
      const surveyId = toNumber(route.query.surveyId);
      return surveyId == null ? undefined : { surveyId };
    }
    case "ExpenseSheetDetailDialog": {
      const id = toNumber(route.params.id);
      const returnModule = queryValue(route.query.returnModule);
      if (id == null && !returnModule) {
        return undefined;
      }
      return { id, returnModule };
    }
    case "ActivityDetailModule": {
      const activityId = toNumber(route.query.activityId);
      return activityId == null ? undefined : { activityId };
    }
    case "DocByCategoryModule": {
      const categoryId = toNumber(route.query.categoryId);
      const categoryName = queryValue(route.query.categoryName) ?? "";
      if (categoryId == null) {
        return undefined;
      }
      return {
        currentCategory: {
          id: categoryId,
          name: categoryName,
        },
      };
    }
    case "VacationsDetailModule": {
      const rawBagId = queryValue(route.query.bagId);
      if (!rawBagId) {
        return undefined;
      }
      if (rawBagId === "null") {
        return { bagId: null };
      }
      const bagId = toNumber(rawBagId);
      return bagId == null ? undefined : { bagId };
    }
    case "HolidayCalendarModule":
    case "EmployeeCalendar": {
      const rid = toNumber(route.query.rid);
      const year = toNumber(route.query.year);
      const month = toNumber(route.query.month);
      const employeeCalendar = route.query.employeeCalendar === "true";
      if (
        rid == null &&
        year == null &&
        month == null &&
        employeeCalendar === false
      ) {
        return undefined;
      }
      return {
        ...(rid == null ? {} : { requestId: rid }),
        ...(year == null ? {} : { year }),
        ...(month == null ? {} : { month }),
        ...(employeeCalendar ? { employeeCalendar: true } : {}),
      };
    }
    default:
      return undefined;
  }
}

function buildLegacyParams(
  params: Record<string, string | undefined>,
): Record<string, string> | undefined {
  const normalized = Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export function getLegacyPlaceRequestFromRoute(
  route: RouteStateLike,
): LegacyPlaceRequest | null {
  const routeName = typeof route.name === "string" ? route.name : null;
  if (!routeName) {
    return null;
  }

  switch (routeName) {
    case "home":
    case "checkin":
    case "news":
    case "documents":
    case "procedures":
    case "vacations":
    case "expensesheets":
    case "profile":
    case "projects":
    case "training":
    case "targethours":
    case "kiosk":
    case "register":
      return { nameToken: routeName };
    case "activitydetail": {
      const activityId = queryValue(route.query.activityId);
      return {
        nameToken: "activitydetail",
        params: buildLegacyParams({
          "activity-id": activityId,
        }),
      };
    }
    case "docbycategory": {
      const categoryId = queryValue(route.query.categoryId);
      if (!categoryId) {
        return null;
      }

      return {
        nameToken: "docbycategory",
        params: {
          categoryId,
          categoryName: queryValue(route.query.categoryName) ?? "",
        },
      };
    }
    case "vacationsdetail": {
      const bagId = queryValue(route.query.bagId);
      return {
        nameToken: "vacationsdetail",
        params: bagId ? { bagId } : undefined,
      };
    }
    case "survey": {
      const surveyId = queryValue(route.query.surveyId);
      return surveyId
        ? {
            nameToken: "survey",
            params: { surveyId },
          }
        : null;
    }
    case "employee": {
      const id = queryValue(route.params.id);
      return {
        nameToken: "employee",
        params: id ? { id } : undefined,
      };
    }
    case "hcalendar":
    case "employeecalendar": {
      const params = Object.fromEntries(
        Object.entries({
          rid: queryValue(route.query.rid),
          employeeCalendar:
            routeName === "employeecalendar"
              ? "true"
              : queryValue(route.query.employeeCalendar),
          year: queryValue(route.query.year),
          month: queryValue(route.query.month),
        }).filter(([, value]) => value != null),
      );

      return {
        nameToken: "hcalendar",
        params: buildLegacyParams(params),
      };
    }
    case "form": {
      const formId = queryValue(route.params.formId);
      return {
        nameToken: "form",
        params: formId ? { formId } : undefined,
      };
    }
    case "review": {
      const params = Object.fromEntries(
        Object.entries({
          reviewId: queryValue(route.params.reviewId),
          phase: queryValue(route.params.phase),
        }).filter(([, value]) => value != null),
      );

      return {
        nameToken: "review",
        params: buildLegacyParams(params),
      };
    }
    default:
      return null;
  }
}

export function normalizeLegacyHash(hash: string): string | null {
  const sanitizedHash = stripAuthResponseFragment(hash);

  if (!sanitizedHash || sanitizedHash === "#/" || sanitizedHash.startsWith("#/")) {
    return null;
  }

  if (!sanitizedHash.startsWith("#")) {
    return null;
  }

  const rawToken = sanitizedHash.substring(1);
  if (!rawToken || rawToken.startsWith("/")) {
    return null;
  }

  const [nameToken, ...rawParams] = rawToken.split(";");
  const params = new URLSearchParams();

  rawParams.forEach((rawParam) => {
    const [rawKey, ...rawValueParts] = rawParam.split("=");
    const key = rawKey?.trim();
    if (!key) {
      return;
    }

    const value = rawValueParts.join("=").trim();
    if (value.length > 0) {
      params.set(key, value);
    }
  });

  switch (nameToken) {
    case "hcalendar": {
      const employeeCalendar = params.get("employeeCalendar") === "true";
      if (employeeCalendar) {
        return "#/employeecalendar";
      }

      const queryString = params.toString();
      return queryString.length > 0
        ? `#/hcalendar?${queryString}`
        : "#/hcalendar";
    }
    case "employee": {
      const id = params.get("id");
      return id ? `#/employee/${id}` : "#/employee";
    }
    case "form": {
      const formId = params.get("formId");
      return formId ? `#/form/${formId}` : "#/form";
    }
    case "review": {
      const reviewId = params.get("reviewId");
      const phase = params.get("phase");
      if (reviewId && phase) {
        return `#/review/${reviewId}/${phase}`;
      }
      if (reviewId) {
        return `#/review/${reviewId}`;
      }
      return "#/review";
    }
    default: {
      const queryString = params.toString();
      return queryString.length > 0
        ? `#/${nameToken}?${queryString}`
        : `#/${nameToken}`;
    }
  }
}

export function stripAuthResponseFragment(hash: string): string {
  if (!hash.startsWith("#")) {
    return hash;
  }

  const authParamIndex = hash.search(
    /&(state|session_state|code|error|error_description)=/i,
  );

  if (authParamIndex < 0) {
    return hash;
  }

  return hash.substring(0, authParamIndex);
}
